import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Styles
import "./styles/App.css";
import "./styles/fonts/fonts.css";

// Core components that should load immediately
import ScrollToTop from "./Components/ScrollToTop/ScrollToTop";
import { FontLoader } from "./utils/FontLoader";
import LoadingSpinner from "./Components/LoadingSpinner/LoadingSpinner";
import GoogleAnalytics from "./Components/Analytics/GoogleAnalytics";
import {
  initScrollbarColorChanger,
  cleanupScrollbarColorChanger,
} from "./utils/scrollbarColor";

// Lazy loaded components with chunk naming
const Navbar = lazy(
  () => import(/* webpackChunkName: "navbar" */ "./Components/Navbar/Navbar"),
);
const SocialMediaNavbar = lazy(
  () =>
    import(
      /* webpackChunkName: "social-navbar" */ "./Components/SocialMediaNavbar/SocialMediaNavbar"
    ),
);
const Footer = lazy(
  () => import(/* webpackChunkName: "footer" */ "./Components/Footer/Footer"),
);

// Lazy loaded pages with chunk naming
const Homepage = lazy(
  () =>
    import(/* webpackChunkName: "home" */ "./Pages/MainPages/Home/Homepage"),
);
const AboutPage = lazy(
  () =>
    import(
      /* webpackChunkName: "about" */ "./Pages/MainPages/AboutPage/AboutPage"
    ),
);
const ContactUs = lazy(
  () =>
    import(
      /* webpackChunkName: "contact" */ "./Pages/MainPages/ContactUs/ContactUs"
    ),
);
const FAQ = lazy(
  () => import(/* webpackChunkName: "faq" */ "./Pages/MainPages/FAQ/FAQ"),
);
const PrivacyPolicy = lazy(
  () =>
    import(
      /* webpackChunkName: "privacy" */ "./Pages/MainPages/PrivacyPolicy/PrivacyPolicy"
    ),
);
const NotFound = lazy(
  () => import(/* webpackChunkName: "not-found" */ "./Pages/NotFound/NotFound"),
);

// Lazy loaded offering pages with chunk naming
const IndividualPage = lazy(
  () =>
    import(
      /* webpackChunkName: "individual" */ "./Pages/OfferingPages/Individual/IndividualPage"
    ),
);
const RelationshipsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "relationships" */ "./Pages/OfferingPages/Relationships/RelationshipsPage"
    ),
);
const GroupsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "groups" */ "./Pages/OfferingPages/Groups/GroupsPage"
    ),
);
const EapPage = lazy(
  () =>
    import(
      /* webpackChunkName: "eap" */ "./Pages/OfferingPages/Businesses/EapPage"
    ),
);

// Preload function for critical routes
const preloadRoutes = () => {
  const preloadComponent = (component) => {
    const preloadPromise = component();
    return preloadPromise;
  };

  // Preload most accessed pages
  preloadComponent(
    () =>
      import(/* webpackChunkName: "home" */ "./Pages/MainPages/Home/Homepage"),
  );
  preloadComponent(
    () => import(/* webpackChunkName: "navbar" */ "./Components/Navbar/Navbar"),
  );
  preloadComponent(
    () => import(/* webpackChunkName: "footer" */ "./Components/Footer/Footer"),
  );
};

// Routes configuration with metadata for preloading
const routes = [
  { path: "/", element: <Homepage />, preload: true },
  { path: "/about-us", element: <AboutPage />, preload: false },
  { path: "/contact-us", element: <ContactUs />, preload: false },
  { path: "/faq", element: <FAQ />, preload: false },
  { path: "/personal-coaching", element: <IndividualPage />, preload: true },
  { path: "/relationships", element: <RelationshipsPage />, preload: false },
  { path: "/groups", element: <GroupsPage />, preload: false },
  { path: "/eap-services", element: <EapPage />, preload: false },
  { path: "/privacy-policy", element: <PrivacyPolicy />, preload: false },
  { path: "*", element: <NotFound />, preload: false },
];

// Router wrapper component with enhanced error boundary
const RouterWrapper = () => {
  return (
    <div className="page-wrapper-max">
      <ScrollToTop />
      <Suspense fallback={<LoadingSpinner />}>
        <ErrorBoundary
          fallback={<div>Something went wrong. Please refresh the page.</div>}
        >
          <Navbar />
          <div className="page-wrapper">
            <div className="main-content">
              <div className="content" id="main-content">
                <ScrollToTop />
                <Routes>
                  {routes.map(({ path, element }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <Suspense fallback={<LoadingSpinner />}>
                          <ErrorBoundary>{element}</ErrorBoundary>
                        </Suspense>
                      }
                    />
                  ))}
                </Routes>
                <SocialMediaNavbar className="hide-socials show-socials-nav" />
              </div>
            </div>
          </div>
          <Footer />
        </ErrorBoundary>
      </Suspense>
    </div>
  );
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // You can log the error to an error reporting service here
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <div>Something went wrong.</div>;
    }
    return this.props.children;
  }
}

// Environment variables are automatically loaded by Create React App
// No need to configure dotenv in browser code

const App = () => {
  useEffect(() => {
    // Initialize font loading
    document.documentElement.classList.add("fonts-loading");
    FontLoader.load().then(() => {
      document.documentElement.classList.remove("fonts-loading");
    });

    // Initialize dynamic scrollbar color changer
    initScrollbarColorChanger();

    // Preload critical routes after initial render
    preloadRoutes();

    // Cleanup on unmount
    return () => {
      cleanupScrollbarColorChanger();
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <GoogleAnalytics />
        <RouterWrapper />
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
